/* Shared building blocks for the per-provider integration vaults. */
import { ApifyScrapeTestPanel } from "./ApifyVault";
import { ZevariProbePanel } from "./ZevariVault";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Loader2, Mail, Linkedin, MessageCircle, ChevronDown, ChevronRight, ExternalLink, Search, BadgeCheck, FlaskConical, Phone, UserSearch, Settings2 } from "lucide-react";

export const CHANNEL_ICON = { linkedin: Linkedin, email: Mail, whatsapp: MessageCircle };

/* ----------------------------------------------------------------- */
/*  AdvancedSection — inklapbare sectie voor proof-tests & extra's.   */
/* ----------------------------------------------------------------- */
export function AdvancedSection({ id, label, children }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    return (
        <div className="mt-4 border-t border-white/8 pt-3">
            <button onClick={() => setOpen((o) => !o)} data-testid={`advanced-toggle-${id}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-white">
                <Settings2 size={12} />
                {label || t("integr.advanced")}
                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
            {open && <div className="mt-2 space-y-4" data-testid={`advanced-body-${id}`}>{children}</div>}
        </div>
    );
}
/* ----------------------------------------------------------------- */
/*  ProofPanel — run a REAL sample call and show tangible evidence    */
/*  (live data returned by the provider) so admins can verify the     */
/*  integration truly works, not just that a key is stored.           */
/* ----------------------------------------------------------------- */
export function ProofPanel({ provider, cfg }) {
    const { t, i18n } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const run = async () => {
        setBusy(true); setError(null);
        try {
            const r = await api.post(`/integrations/${provider.id}/proof`);
            setResult(r.data);
            toast.success(t("integr.proof_ok", { name: provider.name }));
        } catch (e) {
            setResult(null);
            setError(e?.response?.data?.detail || t("integr.proof_failed"));
        } finally { setBusy(false); }
    };

    const locale = i18n.language === "en" ? "en-GB" : i18n.language === "fr" ? "fr-BE" : "nl-BE";

    return (
        <div className="mt-4 rounded-xl border border-[#22c55e]/20 bg-[#22c55e]/[0.03] p-4"
            data-testid={`proof-panel-${provider.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2.5">
                    <FlaskConical size={16} className="text-[#22c55e] mt-0.5 shrink-0" />
                    <div>
                        <div className="text-xs font-mono uppercase tracking-[0.18em] text-[#22c55e]">{t("integr.proof_title")}</div>
                        <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5 max-w-md">{t("integr.proof_sub")}</p>
                    </div>
                </div>
                <button onClick={run} disabled={busy || !cfg.configured}
                    data-testid={`proof-run-${provider.id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#22c55e] text-black text-xs font-medium hover:bg-[#22c55e]/85 disabled:opacity-50">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <BadgeCheck size={12} />}
                    {busy ? t("integr.proof_running") : t("integr.proof_run")}
                </button>
            </div>

            {!cfg.configured && (
                <p className="mt-2 text-[11px] text-[var(--jr-text-3)] italic">{t("integr.proof_not_configured")}</p>
            )}

            {error && (
                <div className="mt-3 rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/[0.08] p-3 text-xs inline-flex items-start gap-2"
                    data-testid={`proof-error-${provider.id}`}>
                    <AlertTriangle size={14} className="text-[#ef4444] mt-0.5 shrink-0" />
                    <span className="text-[var(--jr-text-2)]">{error}</span>
                </div>
            )}

            {result && (
                <div className="mt-3 space-y-3" data-testid={`proof-result-${provider.id}`}>
                    <div className="rounded-lg border border-[#22c55e]/30 bg-[#22c55e]/[0.08] p-3 text-xs flex items-start gap-2">
                        <CheckCircle2 size={14} className="text-[#22c55e] mt-0.5 shrink-0" />
                        <div className="min-w-0">
                            <div className="text-white font-medium">{result.summary}</div>
                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)]">
                                <span>{t("integr.proof_latency")}: <span className="text-[#22c55e]">{result.latency_ms}ms</span></span>
                                {result.query && <span>{t("integr.proof_query")}: <span className="text-[var(--jr-text-2)] normal-case">{result.query}</span></span>}
                                <span>{new Date(result.checked_at).toLocaleString(locale, { dateStyle: "short", timeStyle: "medium" })}</span>
                            </div>
                        </div>
                    </div>
                    {(result.items || []).length > 0 && (
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1.5">
                                {t("integr.proof_evidence")} · {result.items.length}
                            </div>
                            <ul className="space-y-1.5">
                                {result.items.map((it, i) => (
                                    <li key={i} className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 px-3 py-2 text-xs"
                                        data-testid={`proof-item-${provider.id}-${i}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="text-white truncate">{it.title}</div>
                                                {it.subtitle && <div className="text-[11px] text-[var(--jr-text-3)] mt-0.5 line-clamp-2">{it.subtitle}</div>}
                                            </div>
                                            {it.url && (
                                                <a href={it.url} target="_blank" rel="noreferrer"
                                                    className="p-1.5 rounded border border-white/10 text-[var(--jr-info)] hover:text-white hover:bg-white/5 shrink-0">
                                                    <ExternalLink size={11} />
                                                </a>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {(provider.id === "apollo" || provider.id === "surfe") && cfg.configured && (
                <EnrichTestPanel provider={provider} />
            )}
            {provider.id === "apify" && cfg.configured && (
                <ApifyScrapeTestPanel />
            )}
            {provider.id === "zevari" && cfg.configured && (
                <ZevariProbePanel />
            )}
        </div>
    );
}
/* ----------------------------------------------------------------- */
/*  EnrichTestPanel — paste a LinkedIn profile URL, run a REAL        */
/*  enrichment call and show the actual email + phone returned.       */
/*  Only for contact-data providers (Apollo, Surfe).                  */
/* ----------------------------------------------------------------- */
export function EnrichTestPanel({ provider }) {
    const { t } = useTranslation();
    const [url, setUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const run = async () => {
        if (!url.trim()) return;
        setBusy(true); setError(null); setResult(null);
        try {
            const r = await api.post(`/integrations/${provider.id}/enrich-test`, { linkedin_url: url.trim() });
            if (r.data.pending && r.data.enrichment_id) {
                // Surfe enriches async — poll every 3s (up to 2 min) and stream
                // partial profile data into the result card while we wait.
                const t0 = Date.now();
                let final = null;
                for (let i = 0; i < 40; i++) {
                    await new Promise((res) => setTimeout(res, 3000));
                    const pr = await api.get(`/integrations/surfe/enrich-test/${r.data.enrichment_id}`,
                        { params: { linkedin_url: url.trim() } });
                    if (pr.data.person) setResult({ ...pr.data, latency_ms: Date.now() - t0 });
                    if (!pr.data.pending) { final = pr.data; break; }
                }
                if (!final) {
                    setError(t("integr.enrich_test_timeout"));
                } else {
                    toast.success(t("integr.enrich_test_ok", { name: final.person?.name || "—" }));
                }
            } else {
                setResult(r.data);
                toast.success(t("integr.enrich_test_ok", { name: r.data.person?.name || "—" }));
            }
        } catch (e) {
            setError(e?.response?.data?.detail || t("integr.enrich_test_failed"));
        } finally { setBusy(false); }
    };

    const p = result?.person;
    return (
        <div className="mt-3 rounded-xl border border-[var(--jr-info)]/20 bg-[var(--jr-info)]/[0.03] p-4"
            data-testid={`enrich-test-panel-${provider.id}`}>
            <div className="flex items-start gap-2.5">
                <UserSearch size={16} className="text-[var(--jr-info)] mt-0.5 shrink-0" />
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--jr-info)]">{t("integr.enrich_test_title")}</div>
                    <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5 max-w-md">{t("integr.enrich_test_sub")}</p>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
                <input value={url} onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !busy && run()}
                    placeholder={t("integr.enrich_test_placeholder")}
                    data-testid={`enrich-test-input-${provider.id}`}
                    className="flex-1 min-w-0 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[var(--jr-info)]"/>
                <button onClick={run} disabled={busy || !url.trim()}
                    data-testid={`enrich-test-run-${provider.id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-info)] text-black text-xs font-medium hover:bg-[var(--jr-info)]/85 disabled:opacity-50 shrink-0">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                    {busy ? t("integr.enrich_test_running") : t("integr.enrich_test_run")}
                </button>
            </div>

            {error && (
                <div className="mt-3 rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/[0.08] p-3 text-xs inline-flex items-start gap-2"
                    data-testid={`enrich-test-error-${provider.id}`}>
                    <AlertTriangle size={14} className="text-[#ef4444] mt-0.5 shrink-0" />
                    <span className="text-[var(--jr-text-2)]">{error}</span>
                </div>
            )}

            {p && (
                <div className="mt-3 rounded-lg border border-[var(--jr-info)]/25 bg-[var(--jr-surface-2)]/40 p-3"
                    data-testid={`enrich-test-result-${provider.id}`}>
                    <div className="flex items-start gap-3">
                        {p.photo_url ? (
                            <img src={p.photo_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-[var(--jr-info)]/15 grid place-items-center shrink-0">
                                <UserSearch size={16} className="text-[var(--jr-info)]" />
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-white">{p.name}</span>
                                {p.linkedin_url && (
                                    <a href={p.linkedin_url} target="_blank" rel="noreferrer"
                                        className="text-[var(--jr-info)] hover:text-white"><Linkedin size={12}/></a>
                                )}
                                {result.pending ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-amber)]"
                                        data-testid={`enrich-pending-${provider.id}`}>
                                        <Loader2 size={9} className="animate-spin"/> {t("integr.enrich_test_pending")}
                                    </span>
                                ) : (
                                    <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{result.latency_ms}ms</span>
                                )}
                            </div>
                            <div className="text-[11px] text-[var(--jr-text-3)] mt-0.5">
                                {[p.title, p.company, p.location].filter(Boolean).join(" · ")}
                            </div>
                            <div className="mt-2.5 grid sm:grid-cols-2 gap-2">
                                <div className="rounded-lg border border-white/8 bg-black/20 px-3 py-2">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)] inline-flex items-center gap-1"><Mail size={9}/> {t("integr.enrich_test_email")}</div>
                                    {(p.emails || []).length > 0 ? (
                                        (p.emails || []).map((em, i) => (
                                            <div key={i} className="text-xs text-[#22c55e] font-mono mt-0.5 break-all" data-testid={`enrich-email-${provider.id}-${i}`}>{em}</div>
                                        ))
                                    ) : (
                                        <div className="text-xs text-[var(--jr-text-3)] italic mt-0.5">{t("integr.enrich_test_none")}</div>
                                    )}
                                </div>
                                <div className="rounded-lg border border-white/8 bg-black/20 px-3 py-2">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)] inline-flex items-center gap-1"><Phone size={9}/> {t("integr.enrich_test_phone")}</div>
                                    {(p.phones || []).length > 0 ? (
                                        (p.phones || []).map((ph, i) => (
                                            <div key={i} className="text-xs text-[#22c55e] font-mono mt-0.5" data-testid={`enrich-phone-${provider.id}-${i}`}>{ph}</div>
                                        ))
                                    ) : (
                                        <div className="text-xs text-[var(--jr-text-3)] italic mt-0.5">{t("integr.enrich_test_none")}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function CreditMeter({ label, w, highlight = false }) {
    if (!w) return (
        <div className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 p-3 text-xs text-[var(--jr-text-3)] italic">
            {label}: —
        </div>
    );
    const color = w.pct >= 80 ? "var(--jr-error)" : w.pct >= 60 ? "var(--jr-amber)" : "#22c55e";
    return (
        <div className={`rounded-lg border ${highlight ? "border-[var(--jr-brand)]/30 bg-[var(--jr-brand)]/[0.04]" : "border-white/8 bg-[var(--jr-surface-2)]/40"} p-3`}>
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</span>
                <span className="text-[10px] font-mono" style={{ color }}>{w.pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                <div className="h-full transition-all"
                    style={{ width: `${Math.min(100, w.pct)}%`, background: color }} />
            </div>
            <div className="mt-2 flex items-baseline justify-between text-xs">
                <span className="text-white font-medium">{w.used} <span className="text-[var(--jr-text-3)] text-[10px]">/ {w.limit}</span></span>
                <span className="text-[10px] text-[var(--jr-text-3)]">{w.remaining} over</span>
            </div>
        </div>
    );
}
/* ----------------------------------------------------------------- */
/*  Helpers                                                          */
/* ----------------------------------------------------------------- */
export function SubSection({ title, action, children, testid }) {
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface-2)]/40 p-4" data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)]">{title}</h4>
                {action}
            </div>
            {children}
        </div>
    );
}
export function AccountSelect({ channel, accounts, value, onChange }) {
    const { t } = useTranslation();
    const Icon = CHANNEL_ICON[channel];
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] inline-flex items-center gap-1.5">
                <Icon size={11} /> {channel}
            </label>
            <select value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={`account-select-${channel}`}
                className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                <option value="">{t("integr.none_option")}</option>
                {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                ))}
            </select>
            <p className="mt-1 text-[10px] text-[var(--jr-text-3)] truncate">
                {accounts.length === 0 ? t("integr.no_accounts_linked") : t("integr.n_available", { n: accounts.length })}
            </p>
        </div>
    );
}
